import { Fragment, useState, useEffect, useRef, type FC } from 'react';
import { 
  Bus, 
  Shield, 
  MapPin, 
  Bell, 
  Video, 
  CheckCircle, 
  Clock, 
  QrCode, 
  Smartphone,
  ArrowRight,
  Star,
  ChevronDown,
  ChevronUp,
  Phone,
  Mail,
  Award,
  TrendingUp,
  Zap,
  AlertCircle,
  MessageCircle,
  Building2,
  Navigation,
  Heart
} from 'lucide-react';
import AnimatedBackground from '../components/AnimatedBackground';
import ScmtResults from '../components/ScmtResults';
import ScmtTestimonials from '../components/ScmtTestimonials';

// Custom hook for scroll animations
const useScrollAnimation = () => {
  const ref = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => observer.disconnect();
  }, []);

  return { ref, isVisible };
};

const SCMT: FC = () => {
  const [openFaq, setOpenFaq] = useState<number | null>(0);

  const features = [
    {
      icon: MapPin,
      title: 'Real-Time GPS Tracking',
      description: 'Live location with 10-second updates. Parents see exactly where the bus is with accurate ETAs.',
      color: 'from-blue-500 to-blue-600'
    },
    {
      icon: Bell,
      title: 'Smart Notifications',
      description: 'Instant alerts at every step — bus approaching, child boarded, arrived at school, delays, and emergencies.',
      color: 'from-orange-500 to-orange-600'
    },
    {
      icon: QrCode,
      title: 'QR Check-In/Out',
      description: 'Digital verification when students board and exit. No more paper logs or manual counting.',
      color: 'from-green-500 to-green-600'
    },
    {
      icon: Video,
      title: 'Live Video Streaming',
      description: 'Optional add-on for complete peace of mind. Parents can see inside the bus in real-time.',
      color: 'from-purple-500 to-purple-600'
    },
    {
      icon: Smartphone,
      title: 'Parent & Driver Apps',
      description: 'Dedicated mobile apps for everyone — parents track, drivers navigate, both stay connected.',
      color: 'from-pink-500 to-pink-600'
    },
    {
      icon: Shield,
      title: 'Safety First Design',
      description: 'Emergency panic button, speed alerts, route deviations, and complete audit trails.',
      color: 'from-red-500 to-red-600'
    }
  ];

  const faqs = [
    {
      q: "How quickly can we get started with SCMT?",
      a: "Typical implementation takes 2-4 weeks depending on fleet size. This includes setup, data migration, app deployment, and training. Our team provides full support throughout the onboarding process."
    },
    {
      q: "Do parents need to pay for the app?",
      a: "No, the parent app is completely free to download and use. The service cost is covered by the transport operator or educational institution."
    },
    {
      q: "What happens if there's no internet connectivity?",
      a: "The driver app is designed to work offline for core functions like check-in/check-out. Data syncs automatically when connectivity returns, ensuring uninterrupted operations even in remote areas."
    },
    {
      q: "Can we customize notifications for different users?",
      a: "Absolutely! Both operators and parents can configure notification preferences including channels (push, SMS), timing, and language preferences."
    },
    {
      q: "How secure is student data?",
      a: "We use AES-256 encryption, maintain strict access controls, and comply with data protection regulations. Your data is stored securely with regular backups."
    },
    {
      q: "Can parents track multiple children?",
      a: "Yes, parents can link and track multiple children even if they're on different routes or buses — all from a single app interface."
    }
  ];

  const FeatureCard: FC<{ feature: typeof features[0]; index: number }> = ({ feature, index }) => {
    const { ref, isVisible } = useScrollAnimation();
    const Icon = feature.icon;
    
    return (
      <div 
        ref={ref}
        className={`
          group relative overflow-hidden rounded-2xl bg-white border border-gray-100 p-6
          hover:shadow-xl hover:shadow-primary-500/10 hover:-translate-y-1
          transition-all duration-500 ease-out
          ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}
        `}
        style={{ transitionDelay: `${index * 100}ms` }}
      >
        {/* Gradient Background on Hover */}
        <div className={`absolute inset-0 bg-gradient-to-br ${feature.color} opacity-0 group-hover:opacity-5 transition-opacity duration-500`} />
        
        {/* Icon */}
        <div className={`
          relative w-14 h-14 rounded-2xl bg-gradient-to-br ${feature.color}
          flex items-center justify-center mb-4
          group-hover:scale-110 group-hover:rotate-3
          transition-all duration-500 shadow-lg
        `}>
          <Icon className="w-7 h-7 text-white" />
        </div>
        
        {/* Content */}
        <h3 className="relative text-xl font-bold text-navy-950 mb-2 group-hover:text-primary-600 transition-colors">
          {feature.title}
        </h3>
        <p className="relative text-gray-600 leading-relaxed">
          {feature.description}
        </p>
        
        {/* Learn More Link */}
        <div className="relative mt-4 flex items-center gap-2 text-primary-600 font-semibold opacity-0 group-hover:opacity-100 transform translate-x-[-10px] group-hover:translate-x-0 transition-all duration-300">
          <span>Learn more</span>
          <ArrowRight className="w-4 h-4" />
        </div>
      </div>
    );
  };

  // UI Mockup Components for Persona Tabs
  const ParentAppMockup = () => (
    <div className="bg-white rounded-3xl shadow-2xl overflow-hidden border border-gray-200">
      {/* Phone Frame */}
      <div className="bg-gray-900 p-2">
        <div className="bg-white rounded-2xl overflow-hidden">
          {/* App Header */}
          <div className="bg-primary-600 px-4 py-3 flex items-center justify-between">
            <div className="text-white font-semibold">My Child's Bus</div>
            <div className="w-8 h-8 bg-white/20 rounded-full" />
          </div>
          
          {/* Map Area */}
          <div className="h-48 bg-gradient-to-br from-blue-50 to-blue-100 relative">
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-12 h-12 bg-primary-600 rounded-full flex items-center justify-center shadow-lg animate-pulse">
                <Bus className="w-6 h-6 text-white" />
              </div>
            </div>
            <div className="absolute bottom-4 left-4 right-4 bg-white rounded-xl p-3 shadow-lg">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                <span className="text-sm font-medium">Bus is 3 mins away</span>
              </div>
            </div>
          </div>
          
          {/* Timeline */}
          <div className="p-4 space-y-3">
            {[
              { time: '7:30 AM', event: 'Boarded at Home Stop', status: 'completed' },
              { time: '7:45 AM', event: 'Departed from School', status: 'current' },
              { time: '8:00 AM', event: 'Arriving at Home', status: 'pending' }
            ].map((item, i) => (
              <div key={i} className="flex items-center gap-3">
                <div className={`w-2 h-2 rounded-full ${
                  item.status === 'completed' ? 'bg-green-500' : 
                  item.status === 'current' ? 'bg-primary-600 animate-pulse' : 'bg-gray-300'
                }`} />
                <div className="flex-1">
                  <div className="text-xs text-gray-500">{item.time}</div>
                  <div className="text-sm font-medium">{item.event}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  const DriverAppMockup = () => (
    <div className="bg-white rounded-3xl shadow-2xl overflow-hidden border border-gray-200">
      <div className="bg-gray-900 p-2">
        <div className="bg-white rounded-2xl overflow-hidden">
          {/* Driver App Header */}
          <div className="bg-orange-500 px-4 py-3">
            <div className="text-white font-semibold">Trip #128</div>
            <div className="text-white/80 text-xs">St. Mary&apos;s School → Drop-off</div>
          </div>
          
          {/* Navigation Card */}
          <div className="p-4 bg-orange-50">
            <div className="bg-white rounded-xl p-4 shadow-sm">
              <div className="flex items-center gap-3 mb-3">
                <Navigation className="w-5 h-5 text-orange-500" />
                <span className="font-semibold text-sm">Next Stop: Gandhi Nagar</span>
              </div>
              <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                <div className="h-full w-2/3 bg-orange-500 rounded-full" />
              </div>
              <div className="flex justify-between text-xs text-gray-500 mt-1">
                <span>2.5 km</span>
                <span>5 mins</span>
              </div>
            </div>
          </div>
          
          {/* Student List */}
          <div className="p-4 space-y-2">
            <div className="text-sm font-semibold mb-2">Students ({12})</div>
            {[
              { name: 'Riya Sharma', status: 'checked' },
              { name: 'Arjun Kumar', status: 'pending' },
              { name: 'Priya Patel', status: 'pending' }
            ].map((student, i) => (
              <div key={i} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 bg-gray-200 rounded-full" />
                  <span className="text-sm">{student.name}</span>
                </div>
                <div className={`w-6 h-6 rounded-full flex items-center justify-center ${
                  student.status === 'checked' ? 'bg-green-500' : 'bg-gray-300'
                }`}>
                  {student.status === 'checked' && <CheckCircle className="w-4 h-4 text-white" />}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  const OperatorDashboardMockup = () => (
    <div className="bg-white rounded-3xl shadow-2xl overflow-hidden border border-gray-200 p-6">
      {/* Dashboard Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="text-lg font-bold">Operations Dashboard</div>
          <div className="text-sm text-gray-500">Live Fleet Overview</div>
        </div>
        <div className="flex gap-2">
          <div className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-xs font-semibold">24 Active</div>
          <div className="px-3 py-1 bg-yellow-100 text-yellow-700 rounded-full text-xs font-semibold">2 Delayed</div>
        </div>
      </div>
      
      {/* Stats Row */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        {[
          { label: 'Total Trips', value: '48', change: '+12%' },
          { label: 'On Time %', value: '96%', change: '+3%' },
          { label: 'Students', value: '1,240', change: '+8%' }
        ].map((stat, i) => (
          <div key={i} className="bg-gray-50 rounded-xl p-3">
            <div className="text-2xl font-bold text-navy-950">{stat.value}</div>
            <div className="text-xs text-gray-500">{stat.label}</div>
            <div className="text-xs text-green-600 mt-1">{stat.change}</div>
          </div>
        ))}
      </div>
      
      {/* Map Visualization */}
      <div className="h-40 bg-gradient-to-br from-green-50 to-blue-50 rounded-xl relative overflow-hidden">
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="grid grid-cols-4 gap-4">
            {[...Array(8)].map((_, i) => (
              <div 
                key={i} 
                className={`w-3 h-3 rounded-full ${
                  i % 3 === 0 ? 'bg-green-500 animate-pulse' : 'bg-primary-600'
                }`} 
              />
            ))}
          </div>
        </div>
        <div className="absolute bottom-3 left-3 bg-white rounded-lg px-3 py-1 shadow-sm text-xs font-medium">
          Live Tracking
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-white">
      <AnimatedBackground />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 lg:pt-40 lg:pb-32 overflow-hidden bg-gray-50">
        <div className="absolute inset-0 bg-gradient-to-br from-primary-50/50 via-transparent to-accent-orange/5" />
        <div className="container-main relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary-100 rounded-full mb-6 animate-fade-in">
              <Shield className="w-4 h-4 text-primary-600" />
              <span className="text-sm font-medium text-primary-700">Student Transportation Solution</span>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-navy-950 leading-tight mb-6">
              Safe Journeys. <span className="text-primary-600">Happy Parents.</span> <br />
              Smarter Operations.
            </h1>
            <p className="text-lg sm:text-xl text-gray-600 leading-relaxed max-w-3xl mx-auto mb-10">
              The complete student transportation management solution for schools, colleges, and transport operators. 
              Real-time tracking, instant notifications, and intelligent operations—all in one platform.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="#contact" className="btn-primary group">
                Request Demo
              </a>
              <a href="/pricing" className="btn-secondary">
                View Pricing
                <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
              </a>
            </div>
            
            {/* Stats removed - moving to dedicated section */}
          </div>
        </div>
      </section>

      {/* Real Results Section */}
      <ScmtResults />

      {/* Problem/Solution Section */}
      <section className="section-padding bg-white">
        <div className="container-main">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold text-navy-950 mb-6">
                The Challenge with Student Transportation
              </h2>
              <div className="space-y-4">
                {[
                  { icon: AlertCircle, text: 'Parents worry about their child\'s safety throughout the journey' },
                  { icon: Clock, text: 'Operations teams spend 8+ hours weekly on manual scheduling' },
                  { icon: MessageCircle, text: 'Constant phone calls asking "Where is the bus?"' },
                  { icon: TrendingUp, text: 'Rising fuel costs with inefficient routes' },
                ].map((item, index) => (
                  <div key={index} className="flex items-start gap-4 p-4 bg-gray-50 rounded-xl hover:bg-red-50 transition-colors">
                    <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <item.icon className="w-5 h-5 text-red-500" />
                    </div>
                    <p className="text-gray-700">{item.text}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <div className="bg-gradient-to-br from-primary-50 to-accent-orange/5 rounded-2xl p-8 sm:p-12">
                <div className="text-center">
                  <div className="w-20 h-20 bg-primary-600 rounded-2xl flex items-center justify-center mx-auto mb-6">
                    <Bus className="w-10 h-10 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-navy-950 mb-4">SCMT Changes Everything</h3>
                  <div className="space-y-3">
                    {[
                      'Real-time tracking eliminates uncertainty',
                      'Automated rostering saves 8+ hours/week',
                      'Instant notifications reduce calls by 70%',
                      'Route optimization cuts fuel costs by 5-10%'
                    ].map((item, index) => (
                      <div key={index} className="flex items-center gap-3">
                        <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                        <span className="text-gray-700 text-left">{item}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Grid with Animations */}
      <section className="section-padding bg-gray-50">
        <div className="container-main">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-navy-950 mb-4">
              Powerful Features for Complete Control
            </h2>
            <p className="text-lg text-gray-600">
              Everything you need to manage student transportation safely and efficiently.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => (
              <FeatureCard key={index} feature={feature} index={index} />
            ))}
          </div>
        </div>
      </section>

      <section className="section-padding bg-white">
        <div className="container-main">
          <div className="text-center max-w-3xl mx-auto">
            <p className="text-sm text-gray-500 mb-4 uppercase tracking-widest font-medium">Unified Ecosystem</p>
            <h2 className="text-5xl lg:text-6xl font-bold text-navy-950 font-heading leading-[0.9] mb-6">
              TAILORED FOR<br />
              <span className="text-primary-600">EVERY ROLE</span>
            </h2>
            <p className="text-xl text-gray-600 leading-relaxed">
              Dedicated tools designed specifically for the unique needs of parents, drivers, and fleet managers.
            </p>
          </div>
        </div>
      </section>

      <section className="section-padding bg-gray-50 overflow-hidden">
        <div className="container-main">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div className="order-2 lg:order-1">
              <div className="relative">
                <div className="absolute -inset-4 bg-gradient-to-r from-blue-100 to-blue-50 rounded-3xl blur-2xl opacity-70" />
                <div className="relative bg-white rounded-3xl shadow-2xl p-4 border border-gray-100">
                  <ParentAppMockup />
                </div>
              </div>
            </div>
            <div className="order-1 lg:order-2">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-100 rounded-full mb-6">
                <Heart className="w-4 h-4 text-blue-600" />
                <span className="text-sm font-medium text-blue-700">For Parents</span>
              </div>
              <h3 className="text-4xl lg:text-5xl font-bold text-navy-950 font-heading mb-4">
                Peace of Mind in Your Pocket
              </h3>
              <p className="text-xl text-gray-600 mb-8">
                Never wonder where your child is. Track their journey in real-time with instant notifications at every step.
              </p>
              <div className="grid sm:grid-cols-2 gap-4">
                {[
                  { icon: MapPin, text: 'Live track your child\'s bus in real-time' },
                  { icon: Bell, text: 'Get instant notifications at every stop' },
                  { icon: Clock, text: 'View trip history and attendance' },
                  { icon: Shield, text: 'One-tap emergency panic button' },
                ].map((item, index) => {
                  const Icon = item.icon;
                  return (
                    <div key={index} className="flex items-start gap-3 p-4 bg-white rounded-xl shadow-sm">
                      <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Icon className="w-5 h-5 text-blue-600" />
                      </div>
                      <span className="text-gray-700 font-medium text-sm">{item.text}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="section-padding bg-white overflow-hidden">
        <div className="container-main">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-orange-100 rounded-full mb-6">
                <Bus className="w-4 h-4 text-orange-600" />
                <span className="text-sm font-medium text-orange-700">For Drivers</span>
              </div>
              <h3 className="text-4xl lg:text-5xl font-bold text-navy-950 font-heading mb-4">
                Tools That Make Your Job Easier
              </h3>
              <p className="text-xl text-gray-600 mb-8">
                Purpose-built mobile app with navigation, QR scanning, and offline support. Focus on driving safely, we handle the rest.
              </p>
              <div className="grid sm:grid-cols-2 gap-4">
                {[
                  { icon: Navigation, text: 'Turn-by-turn navigation to each stop' },
                  { icon: QrCode, text: 'QR scanner for quick student verification' },
                  { icon: Zap, text: 'Offline mode works without internet' },
                  { icon: AlertCircle, text: 'One-tap emergency reporting' },
                ].map((item, index) => {
                  const Icon = item.icon;
                  return (
                    <div key={index} className="flex items-start gap-3 p-4 bg-gray-50 rounded-xl">
                      <div className="w-10 h-10 bg-orange-50 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Icon className="w-5 h-5 text-orange-600" />
                      </div>
                      <span className="text-gray-700 font-medium text-sm">{item.text}</span>
                    </div>
                  );
                })}
              </div>
            </div>
            <div>
              <div className="relative">
                <div className="absolute -inset-4 bg-gradient-to-r from-orange-100 to-orange-50 rounded-3xl blur-2xl opacity-70" />
                <div className="relative bg-white rounded-3xl shadow-2xl p-4 border border-gray-100">
                  <DriverAppMockup />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="section-padding bg-gray-50 overflow-hidden">
        <div className="container-main">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div className="order-2 lg:order-1">
              <div className="relative">
                <div className="absolute -inset-4 bg-gradient-to-r from-green-100 to-green-50 rounded-3xl blur-2xl opacity-70" />
                <div className="relative bg-white rounded-3xl shadow-sm p-0 border border-gray-100">
                  <OperatorDashboardMockup />
                </div>
              </div>
            </div>
            <div className="order-1 lg:order-2">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-green-100 rounded-full mb-6">
                <TrendingUp className="w-4 h-4 text-green-600" />
                <span className="text-sm font-medium text-green-700">For Operators</span>
              </div>
              <h3 className="text-4xl lg:text-5xl font-bold text-navy-950 font-heading mb-4">
                Scale Your Business Efficiently
              </h3>
              <p className="text-xl text-gray-600 mb-8">
                Manage multiple schools from one dashboard. Automated rostering saves 8+ hours weekly while reducing costs.
              </p>
              <div className="grid sm:grid-cols-2 gap-4">
                {[
                  { icon: Building2, text: 'Manage multiple schools from one dashboard' },
                  { icon: Clock, text: 'Save 8+ hours/week with auto-rostering' },
                  { icon: TrendingUp, text: '5-10% fuel savings with optimized routes' },
                  { icon: Award, text: 'Automated reports and analytics' },
                ].map((item, index) => {
                  const Icon = item.icon;
                  return (
                    <div key={index} className="flex items-start gap-3 p-4 bg-white rounded-xl shadow-sm">
                      <div className="w-10 h-10 bg-green-50 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Icon className="w-5 h-5 text-green-600" />
                      </div>
                      <span className="text-gray-700 font-medium text-sm">{item.text}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="section-padding bg-gray-50">
        <div className="container-main">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-navy-950 mb-4">
              How SCMT Works
            </h2>
            <p className="text-lg text-gray-600">
              From setup to daily operations in 5 simple steps.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-6">
            {[
              { step: '1', title: 'Onboard', desc: 'Set up institutions, routes, vehicles, and bulk import students' },
              { step: '2', title: 'Plan', desc: 'Create routes, assign students, roster drivers and vehicles' },
              { step: '3', title: 'Execute', desc: 'Drivers complete trips while parents track in real-time' },
              { step: '4', title: 'Monitor', desc: 'Operations team monitors all trips and gets alerts' },
              { step: '5', title: 'Analyze', desc: 'Review reports and optimize for better performance' },
            ].map((item, index) => (
              <div key={index} className="text-center group">
                <div className="relative inline-block">
                  <div className="w-16 h-16 bg-primary-600 text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4 shadow-lg group-hover:scale-110 transition-transform">
                    {item.step}
                  </div>
                  {index < 4 && (
                    <div className="hidden lg:block absolute top-1/2 left-full w-full h-0.5 bg-primary-200" />
                  )}
                </div>
                <h3 className="font-bold text-navy-950 mb-2">{item.title}</h3>
                <p className="text-sm text-gray-600">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Modules Section */}
      <section className="section-padding bg-white">
        <div className="container-main">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-navy-950 mb-4">
              Everything You Need to Run a Safer Fleet
            </h2>
            <p className="text-lg text-gray-600">
              A complete operating system for student transportation, organized into powerful modules.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {/* Safety Pillar */}
            <div className="space-y-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                  <Shield className="w-5 h-5 text-red-600" />
                </div>
                <h3 className="text-xl font-bold text-navy-950">Safety & Security</h3>
              </div>
              {[
                { name: 'Video Streaming', desc: 'Live in-bus camera feed' },
                { name: 'Notifications', desc: 'Instant alerts for parents' },
                { name: 'Check-In/Check-Out', desc: 'QR & manual verification' },
                { name: 'Student Management', desc: 'Secure profile & data' }
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-4 p-4 bg-gray-50 rounded-xl hover:bg-white hover:shadow-md transition-all border border-transparent hover:border-gray-100">
                  <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-semibold text-navy-950 text-sm">{item.name}</h4>
                    <p className="text-xs text-gray-500">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Operations Pillar */}
            <div className="space-y-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Zap className="w-5 h-5 text-blue-600" />
                </div>
                <h3 className="text-xl font-bold text-navy-950">Efficient Operations</h3>
              </div>
              {[
                { name: 'Route Optimization', desc: 'AI-powered path planning' },
                { name: 'Smart Rostering', desc: 'Auto-assign drivers & buses' },
                { name: 'Trip Management', desc: 'Real-time execution tracking' },
                { name: 'No-Show Management', desc: 'Handle absent students' }
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-4 p-4 bg-gray-50 rounded-xl hover:bg-white hover:shadow-md transition-all border border-transparent hover:border-gray-100">
                  <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-semibold text-navy-950 text-sm">{item.name}</h4>
                    <p className="text-xs text-gray-500">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Management Pillar */}
            <div className="space-y-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-green-600" />
                </div>
                <h3 className="text-xl font-bold text-navy-950">Strategic Control</h3>
              </div>
              {[
                { name: 'Operations Dashboard', desc: 'Bird\'s-eye fleet view' },
                { name: 'Institution Management', desc: 'Multi-school support' },
                { name: 'Reports & Analytics', desc: 'Data-driven insights' },
                { name: 'Service Requests', desc: 'Parent request handling' }
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-4 p-4 bg-gray-50 rounded-xl hover:bg-white hover:shadow-md transition-all border border-transparent hover:border-gray-100">
                  <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-semibold text-navy-950 text-sm">{item.name}</h4>
                    <p className="text-xs text-gray-500">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Social Proof - Auto-Scrolling Marquee */}
      <section className="py-12 bg-gray-50 border-y border-gray-100 overflow-hidden">
        <div className="container-main">
          <div className="text-center mb-10">
            <p className="text-sm font-medium text-gray-500 uppercase tracking-wide">Trusted by Leading Schools & Transport Operators</p>
          </div>
          
          {/* Marquee Animation */}
          <div className="relative flex overflow-x-hidden group">
            <div className="animate-marquee flex gap-12 items-center whitespace-nowrap py-2">
              {[...Array(2)].map((_, i) => (
                <Fragment key={i}>
                  {[
                    { name: 'St. Mary\'s School', initials: 'SM' },
                    { name: 'Delhi Public School', initials: 'DPS' },
                    { name: 'City Transport Co', initials: 'CT' },
                    { name: 'Sunrise Logistics', initials: 'SL' },
                    { name: 'Modern School', initials: 'MS' },
                    { name: 'Green Valley Transport', initials: 'GV' },
                    { name: 'Ryan International', initials: 'RI' },
                    { name: 'Global Tech Park', initials: 'GTP' },
                  ].map((logo, index) => (
                    <div key={index} className="flex items-center gap-3 opacity-60 hover:opacity-100 transition-opacity cursor-pointer mx-4">
                      <div className="w-12 h-12 bg-white rounded-xl shadow-sm border border-gray-100 flex items-center justify-center">
                        <span className="text-lg font-bold text-gray-400">{logo.initials}</span>
                      </div>
                      <span className="text-lg font-medium text-gray-500">{logo.name}</span>
                    </div>
                  ))}
                </Fragment>
              ))}
            </div>
          </div>

          <div className="flex flex-wrap justify-center gap-6 lg:gap-12 mt-12">
            {[
              { icon: Star, text: '4.9/5 App Store Rating', color: 'text-yellow-500' },
              { icon: Award, text: 'Best EdTech Solution 2024', color: 'text-primary-600' },
              { icon: TrendingUp, text: '95% Parent Satisfaction', color: 'text-green-600' },
            ].map((badge, index) => (
              <div key={index} className="flex items-center gap-2 bg-white px-4 py-2 rounded-full shadow-sm border border-gray-100">
                <badge.icon className={`w-5 h-5 ${badge.color}`} />
                <span className="text-sm font-semibold text-gray-700">{badge.text}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials - Premium Carousel */}
      <ScmtTestimonials />

      {/* FAQ Section */}
      <section className="section-padding bg-gray-50">
        <div className="container-main">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-navy-950 mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-lg text-gray-600">
              Everything you need to know about SCMT.
            </p>
          </div>
          <div className="max-w-3xl mx-auto space-y-4">
            {faqs.map((faq, index) => (
              <div key={index} className="bg-white rounded-xl shadow-sm overflow-hidden">
                <button
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-gray-50 transition-colors"
                >
                  <span className="font-semibold text-navy-950">{faq.q}</span>
                  {openFaq === index ? (
                    <ChevronUp className="w-5 h-5 text-gray-400" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-gray-400" />
                  )}
                </button>
                {openFaq === index && (
                  <div className="px-6 pb-4">
                    <p className="text-gray-600">{faq.a}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Teaser */}
      <section className="section-padding bg-white">
        <div className="container-main">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-navy-950 mb-4">
              Flexible Plans for Every Fleet Size
            </h2>
            <p className="text-lg text-gray-600">
              From small operators to large fleets, we have a plan that fits your needs.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {[
              {
                name: 'Starter',
                description: 'For small operators with up to 10 vehicles',
                features: ['Core tracking features', 'Parent & driver apps', 'Basic notifications', 'Email support']
              },
              {
                name: 'Professional',
                description: 'For growing operators with 10-50 vehicles',
                features: ['Everything in Starter', 'Advanced rostering', 'No-show management', 'Service requests', 'Priority support'],
                popular: true
              },
              {
                name: 'Enterprise',
                description: 'For large operators with 50+ vehicles',
                features: ['Everything in Professional', 'Video streaming add-on', 'Custom integrations', 'Dedicated account manager', 'SLA guarantee']
              }
            ].map((plan, index) => (
              <div key={index} className={`card p-6 relative ${plan.popular ? 'border-2 border-primary-500' : ''}`}>
                {plan.popular && (
                  <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                    <span className="bg-primary-600 text-white text-xs font-semibold px-3 py-1 rounded-full">Most Popular</span>
                  </div>
                )}
                <h3 className="text-xl font-bold text-navy-950 mb-2">{plan.name}</h3>
                <p className="text-gray-600 text-sm mb-6">{plan.description}</p>
                <ul className="space-y-3 mb-6">
                  {plan.features.map((feature, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm">
                      <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                      <span className="text-gray-700">{feature}</span>
                    </li>
                  ))}
                </ul>
                <button className={`w-full ${plan.popular ? 'btn-primary' : 'btn-secondary'}`}>
                  Contact Sales
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section id="contact" className="section-padding bg-navy-950">
        <div className="container-main">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
              Ready to Transform Your Student Transportation?
            </h2>
            <p className="text-lg text-gray-400 mb-10">
              Join 500+ transport operators who trust SCMT to keep students safe and parents informed. 
              Start your free trial today.
            </p>
            
            <div className="grid sm:grid-cols-3 gap-6 mb-10">
              <div className="bg-white/10 rounded-xl p-6 text-center hover:bg-white/20 transition-colors">
                <Phone className="w-8 h-8 text-primary-400 mx-auto mb-3" />
                <div className="text-white font-medium mb-1">Call Us</div>
                <div className="text-gray-400 text-sm">+91 99165 00454</div>
              </div>
              <div className="bg-white/10 rounded-xl p-6 text-center hover:bg-white/20 transition-colors">
                <Mail className="w-8 h-8 text-primary-400 mx-auto mb-3" />
                <div className="text-white font-medium mb-1">Email Us</div>
                <div className="text-gray-400 text-sm">info@gaadimatic.com</div>
              </div>
              <div className="bg-white/10 rounded-xl p-6 text-center hover:bg-white/20 transition-colors">
                <Award className="w-8 h-8 text-primary-400 mx-auto mb-3" />
                <div className="text-white font-medium mb-1">Free Trial</div>
                <div className="text-gray-400 text-sm">14 days, no credit card</div>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="#contact" className="btn-primary text-lg px-10 inline-flex items-center justify-center">
                Request Demo
              </a>
              <a href="/pricing" className="inline-flex items-center justify-center px-8 py-4 text-base font-semibold text-white border-2 border-white/30 rounded-lg transition-all duration-200 hover:bg-white/10">
                View Pricing
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default SCMT;
